import React from 'react';
import { formatMoveInDate } from '../../utils/formatters';
import { normalizeTypeName } from '../../utils/typeName';
import { PriceTag } from '../../design-system/components';
import { Property } from '../../services/api';
import { formatAreaCell } from './helpers';

export type KvRow = { key: string; value: React.ReactNode; pending?: boolean; emphasize?: boolean };

// 기본단지정보 표 (사용자 피드백 2026-05-21 / 벤치마크 reby24.com/sale/585):
// 모든 메타데이터를 단일 표로. 분양가는 표의 마지막 행에 강조 색상 (accent) 으로 배치.
export const buildKvRows = (property: Property): KvRow[] => {
  // 평형 라벨 — type_prices 우선, 없으면 floor_plans 의 unique type_name (예: "84A · 84B · 142")
  // normalizeTypeName 으로 '084.9918A' → '84A' 안전망 처리.
  const typeLabels: string[] = (() => {
    const seen = new Set<string>();
    const out: string[] = [];
    const push = (raw: any) => {
      const t = normalizeTypeName(raw);
      if (!t || seen.has(t)) return;
      seen.add(t);
      out.push(t);
    };
    ((property as any).type_prices ?? []).forEach((p: any) => push(p.type_name));
    if (out.length === 0) {
      ((property as any).floor_plans ?? []).forEach((p: any) => push(p.type_name));
    }
    return out;
  })();

  const buildingScale = (property as any).building_scale;
  const parkingSpaces = (property as any).parking_spaces;
  const contactNumber = (property as any).contact_number;
  const businessType = (property as any).business_type;
  const propertyType = (property as any).property_type;
  return [
    {
      key: '유형',
      value: propertyType || null,
      pending: !propertyType,
    },
    {
      key: '분양방식',
      value: businessType || null,
      pending: !businessType,
    },
    {
      key: '위치',
      value: property.address || null,
      pending: !property.address,
    },
    {
      key: '규모',
      value: buildingScale || null,
      pending: !buildingScale,
    },
    {
      key: '세대수',
      value: property.total_units && property.total_units > 0
        ? <span className="tabular-nums">{property.total_units.toLocaleString()}<span className="text-[12px] font-semibold text-muted">세대</span></span>
        : null,
      pending: !property.total_units || property.total_units === 0,
    },
    {
      key: '평형',
      value: typeLabels.length > 0
        ? (
          <span className="tabular-nums">
            {typeLabels.slice(0, 5).join(' · ')}
            {typeLabels.length > 5 && <span className="text-[12px] font-semibold text-muted"> 외 {typeLabels.length - 5}</span>}
          </span>
        )
        : (formatAreaCell(property.min_area, property.max_area)
          ? <>{formatAreaCell(property.min_area, property.max_area)}</>
          : null),
      pending: typeLabels.length === 0 && !(property.min_area || property.max_area),
    },
    {
      key: '주차',
      value: parkingSpaces
        ? (
          <span className="tabular-nums">
            {Number(parkingSpaces).toLocaleString()}<span className="text-[12px] font-semibold text-muted">대</span>
            {property.total_units ? (
              <span className="text-[12px] font-semibold text-muted"> (세대당 {(parkingSpaces / property.total_units).toFixed(1)}대)</span>
            ) : null}
          </span>
        )
        : null,
      pending: !parkingSpaces,
    },
    {
      key: '시공사',
      value: property.constructor || null,
      pending: !property.constructor,
    },
    {
      key: '시행사',
      value: property.developer || null,
      pending: !property.developer,
    },
    {
      key: '입주',
      value: formatMoveInDate((property as any).move_in_date) || ((property as any).move_in_date ?? null),
      pending: !((property as any).move_in_date),
    },
    {
      key: '분양문의',
      value: contactNumber && contactNumber !== '1533-8124' ? contactNumber : null,
      pending: !contactNumber || contactNumber === '1533-8124',
    },
    {
      key: '분양가',
      value: <PriceTag size="md" minPrice={property.min_price} maxPrice={property.max_price} emphasize />,
      emphasize: true,
    },
  ];
};
